import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/** Settings → Shipping and delivery → ShipStation. The key and secret are never sent back: only the last 4. */
export type ShipStationStatus = {
  connected: boolean
  readable?: boolean
  enabled: boolean
  api: "v1" | "v2" | null
  api_label?: string
  key_hint?: string
  has_secret?: boolean
  warehouse_id?: string | null
  /** ShipStation store new orders are created in (v1 only); id null = Manual Orders (no store). */
  store?: { id: number | null; name: string | null }
  /** url is null unless you're the owner / have full permissions (it carries the webhook token). */
  webhook?: { url: string | null; subscribed: boolean }
  connected_at?: string
  last_poll_at?: string | null
  last_test?: { at: string; ok: boolean | null } | null
  counts?: { failed?: number; pending?: number; awaiting?: number }
  can_manage: boolean
}
type StatusResponse = { shipstation: ShipStationStatus; webhook_error?: string | null }

const KEY = ["settings", "shipstation"]

export function useShipStation() {
  return useQuery({ queryKey: KEY, queryFn: () => sdk.client.fetch<StatusResponse>("/admin/terify/settings/shipstation") })
}

function useShipStationMutation<V>(fn: (v: V) => Promise<StatusResponse | unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r: any) => {
      if (r?.shipstation) qc.setQueryData(KEY, r)
      else qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export const useConnectShipStation = () =>
  useShipStationMutation((body: { api_key: string; api_secret?: string | null; enabled?: boolean }) => sdk.client.fetch<StatusResponse>("/admin/terify/settings/shipstation", { method: "POST", body }))
export const useDisconnectShipStation = () => useShipStationMutation(() => sdk.client.fetch<StatusResponse>("/admin/terify/settings/shipstation", { method: "DELETE" }))
export const useShipStationEnabled = () => useShipStationMutation((enabled: boolean) => sdk.client.fetch<StatusResponse>("/admin/terify/settings/shipstation/enabled", { method: "POST", body: { enabled } }))
export const useTestShipStation = () => useShipStationMutation(() => sdk.client.fetch<{ test: { ok: boolean; message: string } }>("/admin/terify/settings/shipstation/connection-test", { method: "POST", body: {} }))
export const useSyncShipStation = () =>
  useShipStationMutation(() => sdk.client.fetch<{ sync: { sent: Record<string, number>; shipments: Record<string, number> & { error?: string } } }>("/admin/terify/settings/shipstation/poll", { method: "POST", body: {} }))

/** One order's ShipStation state (order page). */
export type OrderShipStation = {
  integration: { connected: boolean; enabled: boolean; api: "v1" | "v2" | null }
  order: {
    status: "pending" | "sending" | "cancelling" | "sent" | "partially_shipped" | "shipped" | "cancelled" | "failed" | "not_needed"
    shipstation_order_id: string | null
    /** ShipStation store the order was created in (null = Manual Orders / sent before stores). */
    store_id?: number | null
    api: string | null
    last_error: string | null
    attempts: number
    next_attempt_at: string | null
    sent_at: string | null
    shipped_at: string | null
    cancelled_at: string | null
  } | null
  shipments: { id: string; status: string; fulfillment_id: string | null; tracking_number: string | null; carrier: string | null; source: string | null; created_at: string }[]
}

export function useOrderShipStation(orderId: string) {
  return useQuery({
    queryKey: ["order", orderId, "shipstation"],
    queryFn: () => sdk.client.fetch<{ shipstation: OrderShipStation }>(`/admin/terify/orders/${orderId}/shipstation`),
    // While a send is in flight, look again shortly.
    refetchInterval: (q) => (["pending", "sending", "cancelling"].includes((q.state.data as any)?.shipstation?.order?.status) ? 4000 : false),
  })
}

export function useResendToShipStation(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch<{ shipstation: OrderShipStation }>(`/admin/terify/orders/${orderId}/shipstation`, { method: "POST", body: {} }),
    onSuccess: (r) => qc.setQueryData(["order", orderId, "shipstation"], r),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] })
      qc.invalidateQueries({ queryKey: ["activity"] })
    },
  })
}

/** Settings → ShipStation → Shipping defaults (backend lib/shipstation/shipping-prefs.ts). */
export type ShippingRule = { match: string; carrier_code: string; carrier_id?: string | null; service_code: string; label?: string | null }
export type PackageSize = { sku: string; length: number; width: number; height: number }
export type ShippingPrefs = { services: ShippingRule[]; ship_from: { id: string; label?: string | null } | null; packages: PackageSize[] }
export type ShippingDefaults = {
  prefs: ShippingPrefs
  /** The checkout's shipping choices. */
  methods: string[]
  options: {
    carriers: { code: string; id: string | null; name: string; services: { code: string; name: string; domestic: boolean }[] }[]
    warehouses: { id: string; name: string; address: string; is_default: boolean }[]
  } | null
  options_error: string | null
}
const SHIP_KEY = ["settings", "shipstation", "shipping"]
export function useShippingDefaults(enabled: boolean) {
  return useQuery({ queryKey: SHIP_KEY, enabled, staleTime: 60_000, queryFn: () => sdk.client.fetch<{ shipping: ShippingDefaults }>("/admin/terify/settings/shipstation/shipping") })
}
export function useSaveShippingDefaults() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ShippingPrefs) => sdk.client.fetch<{ shipping: ShippingDefaults }>("/admin/terify/settings/shipstation/shipping", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(SHIP_KEY, r),
  })
}

/** Settings → ShipStation → Store (backend lib/shipstation/stores.ts). */
export type ShipStationStoreOption = { storeId: number; storeName: string; marketplaceName: string | null; active: boolean }
export type ShipStationStores = {
  /** false for a v2 key (stores are a v1 thing) or when not connected. */
  supported: boolean
  store_id: number | null
  store_name: string | null
  stores: ShipStationStoreOption[]
  error: string | null
}
const STORES_KEY = ["settings", "shipstation", "stores"]
export function useShipStationStores(enabled: boolean) {
  return useQuery({ queryKey: STORES_KEY, enabled, staleTime: 60_000, queryFn: () => sdk.client.fetch<{ stores: ShipStationStores }>("/admin/terify/settings/shipstation/stores") })
}
export function useSaveShipStationStore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (store_id: number | null) => sdk.client.fetch<{ stores: ShipStationStores }>("/admin/terify/settings/shipstation/store", { method: "POST", body: { store_id } }),
    onSuccess: (r) => {
      qc.setQueryData(STORES_KEY, r)
      qc.invalidateQueries({ queryKey: KEY, exact: true })
    },
  })
}
